import React, { useContext } from 'react'
import styled from 'styled-components'
import {
  margin,
  marginSizes,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import Divider from '@jarvis.oddle/core/components/Divider'
import Form from '@jarvis.oddle/core/components/Form'
import { P, Text } from '@jarvis.oddle/core/components/Typo'
import {
  CardHeader,
  Section
} from 'src/components/molecules/CheckoutComponents/Elements'
import IconCutlery from '@jarvis.oddle/core/components/Icons/IconCutlery'
import Flex from '@jarvis.oddle/core/components/Flex'
import {
  InlineNotification,
  notificationStates
} from '@jarvis.oddle/core/components/InlineNotification'
import htmlParser from 'html-react-parser'
import Switch from '@jarvis.oddle/core/components/Switch'
import { isObjectEmpty } from 'src/utils/object'
import ContactDetails from './ContactDetails'
import PickupAddress from './PickupAddress'
import DeliveryAddress from './DeliveryAddress'
import { isEmpty } from 'src/utils/string'
import { getPhoneError } from '@jarvis.oddle/core/tools/validation'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import { addressConfig } from 'src/components/molecules/CheckoutComponents/BillingAddress'
import { getOrderedAddressFields } from '@jarvis.oddle/core/tools/address'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectDetailMenuSite } from 'src/redux/meta'
import {
  selectCheckout,
  storeCheckoutAddressAction,
  updateDeliveryDetailsAction
} from 'src/redux/checkout'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import { makeQueryString, redirect } from 'src/utils/routes'
import Router, { useRouter } from 'next/router'
import { getCountryNameByDialCode } from 'src/utils/localisation'

const FormTextArea = styled(Form.TextArea)`
  resize: none;
  border: none;
`

const StyledSubTitle = styled(P)`
  margin-bottom: ${margin[marginSizes.medium]};
`

const StyledCutlery = styled(Flex)`
  padding-bottom: ${padding[paddingSizes.medium]};
`

const StyledFlex = styled(Flex).attrs(() => ({
  alignItems: 'center'
}))``

const StyledIconCutlery = styled(IconCutlery)`
  margin: ${margin[marginSizes.small]};
`

const StyledFormSubmitButton = styled(Form.SubmitButton)`
  width: 100%;
  margin: ${margin[marginSizes.none]} ${margin[marginSizes.medium]};
  ${breakpoint('tablet')`
    ${(props) => ({ width: props.width })}
  `}
`

interface AdditionalInfoProps {
  additionalInstructions: string
}

const AdditionalInfo = ({
  additionalInstructions
}: AdditionalInfoProps): JSX.Element => {
  const { t } = useContext(TranslationContext)
  const showCutlery = false

  return (
    <Section>
      <CardHeader bold>{t('Additional Information')}</CardHeader>
      {additionalInstructions && (
        <InlineNotification lightTheme state={notificationStates.informative}>
          {htmlParser(additionalInstructions)}
        </InlineNotification>
      )}
      {showCutlery && (
        <React.Fragment>
          <StyledSubTitle bold>{t('Cutlery')}</StyledSubTitle>
          <StyledCutlery justifyContent="space-between" alignItems="center">
            <StyledFlex>
              <StyledIconCutlery height={26} width={26}/>
              <Text as="label">
                {t(
                  'No cutlery will be provided.\nThank you for saving the environment!'
                )}
              </Text>
            </StyledFlex>
            <Switch onChange={null} controlled/>
          </StyledCutlery>
        </React.Fragment>
      )}
      <StyledSubTitle bold>{t('Customer remarks')}</StyledSubTitle>
      <FormTextArea
        name="additionalRemarks"
        label={t(
          'Enter any order related instructions here e.g. No chili please'
        )}
      />
    </Section>
  )
}

interface CheckoutAddressProps {
  user: {
    data: {
      firstName: string
      lastName: string
      phone: string
      email: string
    }
  }
  storeOrder: {
    data?: Oms.V4.StoreOrder
  }
  nonTW: boolean
  addressFormat: string
  addressRequiredFields: string
  additionalInstructions: string
  initialValues
  dialCode: string
}

const CheckoutAddress = ({
  user,
  storeOrder = {},
  nonTW = true,
  addressFormat,
  addressRequiredFields,
  additionalInstructions,
  initialValues,
  dialCode
}: CheckoutAddressProps): JSX.Element => {
  const { t } = useContext(TranslationContext)
  const dispatch = useAppDispatch()
  const metaState = useAppSelector(selectDetailMenuSite)
  const checkoutState = useAppSelector(selectCheckout)
  const countryName = getCountryNameByDialCode(dialCode)

  const { addressTwoFormat } = metaState.checkoutAddressFormat
  const { deliveryAddressTwoDetails, deliveryAddressTwo } =
  checkoutState.meta.data.storeOrder || {}
  const isPickup = storeOrder.data?.isPickup
  const router = useRouter()
  const { locale, storeOrderId } = router.query as Record<string, string>

  const validQuery = Object.keys(router.query)
  .filter(
    (query) => !['locale', 'storeOrderId', 'step_address'].includes(query)
  )
  .reduce(
    (obj, query) => ({
      ...obj,
      [query]: router.query[query]
    }),
    {}
  )
  const preservedQuery = `?${new URLSearchParams(validQuery).toString()}`
  const query = preservedQuery
    ? `${preservedQuery}&step_payment=true`
    : '?step_payment=true'

  const stepAddressFieldsRequired = addressTwoFormat.reduce(
    (items, item) => ({
      ...items,
      [item.type]: item.fields.reduce(
        (fields, field) => ({ ...fields, [field.name]: field }),
        {}
      )
    }),
    {}
  )

  const onFormSubmit = React.useCallback(
    async (values, { validateForm, setFieldTouched, setFieldError }) => {
      const error = await validateForm()
      if (!isObjectEmpty(error)) {
        Object.keys(error).forEach((key) => {
          setFieldError(key, error[key])
          setFieldTouched(key, true)
        })
        return
      }
      const { addressType } = values
      if (router.query.storeOrderId !== 'preview') {
        await dispatch(
          updateDeliveryDetailsAction({
            storeOrderId: storeOrder.data?.id,
            deliveryAddressType: addressType,
            deliveryAddressTwoDetails: Object.keys(
              stepAddressFieldsRequired[addressType] || {}
            ).reduce(
              (obj, field) => ({
                ...obj,
                [field]: values[field]
              }),
              {}
            ),
            deliveryInstructions: values.deliveryInstructions
          })
        )
        const orderedAddressFields = getOrderedAddressFields(
          addressFormat,
          addressRequiredFields
        )
        await dispatch(
          storeCheckoutAddressAction({
            storeOrderId: storeOrder.data.id,
            organisationName: values.organisationName,
            recipientCheckbox: values.recipientCheckbox,
            ...(values.recipientCheckbox ? {
              recipientName: values.recipientName,
              recipientContact: values.recipientContact,
            } : {}),
            billingAddressDifferentCheckbox:
            values.billingAddressDifferentCheckbox,
            ...(values.billingAddressDifferentCheckbox
              ? orderedAddressFields.reduce(
                (obj, item) => ({
                  ...obj,
                  [addressConfig[item.field](t).billingFieldName]:
                    values[addressConfig[item.field](t).billingFieldName]
                }),
                {}
              )
              : {}),
            additionalRemarks: values.additionalRemarks
          })
        )
        redirect({
          url: `/[locale]/checkout/[storeOrderId]`,
          as: `/${locale}/checkout/${storeOrderId}${query}`,
          shallow: true
        })
      } else {
        Router.push(
          `/${router.query.locale}/checkout/preview${makeQueryString({
            previewToken: router.query.previewToken,
            previewMode: true,
            step_payment: true
          })}`
        )
      }
    },
    [
      addressFormat,
      addressRequiredFields,
      dispatch,
      locale,
      query,
      stepAddressFieldsRequired,
      storeOrder.data?.id,
      storeOrderId,
      t,
      router.query
    ]
  )

  const onFormValidate = React.useCallback(
    (values) => {
      const {
        addressRequiredFields,
        additionalInstructionsFlag: isAdditionalRemarksRequired
      } = checkoutState.meta.data
      const storeOrder = { data: checkoutState.meta.data.storeOrder }
      const user = { data: checkoutState.meta.data.customer }

      if (values['recipientContact'] === null) {
        values['recipientContact'] = ''
      }

      const {
        addressType,
        recipientCheckbox,
        recipientName,
        recipientContact,
        billingAddressCheckbox,
        billingAddressOne,
        billingAddressTwo,
        billingAddressState,
        billingAddressCity,
        billingAddressPostal,
        additionalRemarks
      } = values

      const errors = {} as {
        phone: string
        recipientName: string
        recipientContact: string
        additionalRemarks: string
        termsError: string
        paymentMethod: string
        cardName: string
        card: string
        billingAddressOne: string
        billingAddressTwo: string
        billingAddressCity: string
        billingAddressState: string
        billingAddressPostal: string
        selector: string
      }

      if (!user.data.phone) {
        const phoneError = getPhoneError(t, values.phone)
        if (phoneError) errors.phone = phoneError
      }

      if (recipientCheckbox === true) {
        const recipientContactError = getPhoneError(t, recipientContact)
        if (isEmpty(recipientName)) {
          errors.recipientName = t('Please enter valid recipient name')
        }
        if (isEmpty(recipientContact)) {
          errors.recipientContact = t('Please enter recipient contact number')
        } else if (recipientContactError) {
          errors.recipientContact = recipientContactError
        } else if (!storeOrder.data.isPickup && !recipientContact.includes(dialCode)) {
          errors.recipientContact = t('Please use _countryName_ phone number', { countryName })
        }
      }

      if (!storeOrder.data.isPickup && billingAddressCheckbox !== true) {
        const requiredFields = addressRequiredFields
        .replace(/Header/g, '')
        .replace(/ /g, '')
        .split(',')
        if (requiredFields.includes('address') && isEmpty(billingAddressOne)) {
          errors.billingAddressOne = t('Street name cannot be empty')
        }
        if (
          requiredFields.includes('addressTwo') &&
          isEmpty(billingAddressTwo)
        ) {
          errors.billingAddressTwo = t('Unit number cannot be empty')
        }
        if (
          requiredFields.includes('addressCity') &&
          isEmpty(billingAddressCity)
        ) {
          errors.billingAddressCity = t('City cannot be empty')
        }
        if (
          requiredFields.includes('addressState') &&
          isEmpty(billingAddressState)
        ) {
          errors.billingAddressState = t('State cannot be empty')
        }
        if (
          requiredFields.includes('addressPostal') &&
          isEmpty(billingAddressPostal)
        ) {
          errors.billingAddressPostal = t('Postal code cannot be empty')
        }
      }

      if (isAdditionalRemarksRequired === true) {
        if (isEmpty(additionalRemarks)) {
          errors.additionalRemarks = t('Customer remarks cannot be empty')
        }
      }

      if (
        !storeOrder.data.isPickup &&
        !isObjectEmpty(stepAddressFieldsRequired[addressType] || {})
      ) {
        Object.keys(stepAddressFieldsRequired[addressType])
        .filter(
          (field) =>
            stepAddressFieldsRequired[addressType][field].required &&
            isEmpty(values[field])
        )
        .forEach((field) => {
          errors[field] = t('Please enter your _object_', {
            object: t(field)
          })
        })
      }

      if (!storeOrder.data.isPickup) {
        if (stepAddressFieldsRequired[addressType]) {
          Object.keys(stepAddressFieldsRequired[addressType] || {})
          .filter(
            (field) =>
              stepAddressFieldsRequired[addressType][field].required &&
              isEmpty(values[field])
          )
          .forEach((field) => {
            errors[field] = t('Please enter your _object_', {
              object: t(field)
            })
          })
        } else {
          errors.selector = t('Please select an Address Type')
        }
      }

      return errors
    },
    [checkoutState.meta.data, countryName, dialCode, stepAddressFieldsRequired, t]
  )

  return (
    <Form
      enableReinitialize
      initialValues={{
        ...initialValues,
        addressType:
          initialValues.deliveryAddressType || addressTwoFormat?.[0]?.type,
        // ex: stepAddressFieldsRequired[addressType] = { unitNumber: {}, buildingName: {} }
        // check if deliveryAddressTwoDetails.unitNumber has value or not, if not set it's value is deliveryAddressTwo's value
        // check if deliveryAddressTwoDetails.buildingName has value or not, if not set it's value is ''
        ...Object.keys(
          stepAddressFieldsRequired[
          initialValues.deliveryAddressType || addressTwoFormat?.[0]?.type
            ] || {}
        ).reduce(
          (obj, field, index) => ({
            ...obj,
            ...(deliveryAddressTwoDetails || {})[field] && (deliveryAddressTwoDetails || {})[field] !== '-' ? {
              [field]: (deliveryAddressTwoDetails || {})[field]
            } : index === 0 && deliveryAddressTwo && !deliveryAddressTwo.includes('-,') ? {
              [field]: deliveryAddressTwo
            } : {
              [field]: ''
            },
          }),
          {}
        )
      }}
      onSubmit={onFormSubmit}
      onValidate={onFormValidate}
    >
      <Section>
        <CardHeader bold>
          {isPickup ? t('Pickup Store Address') : t('Delivery Address')}
        </CardHeader>
        {isPickup ? (
          <PickupAddress
            storeName={storeOrder.data?.storeName}
            storeAddress={storeOrder.data?.storeAddress}
          />
        ) : (
          <DeliveryAddress
            stepAddressFieldsRequired={stepAddressFieldsRequired}
            addressTwoFormat={addressTwoFormat}
          />
        )}
      </Section>
      <Divider/>
      <ContactDetails
        user={user}
        nonTW={nonTW}
        isPickup={isPickup}
        addressFormat={addressFormat}
        addressRequiredFields={addressRequiredFields}
      />
      <Divider/>
      <AdditionalInfo additionalInstructions={additionalInstructions}/>
      <Flex justifyContent="center">
        <StyledFormSubmitButton width="300px">
          {t('Continue to Payment')}
        </StyledFormSubmitButton>
      </Flex>
    </Form>
  )
}

export default CheckoutAddress
